import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import {
    NgbOffcanvas,
    NgbOffcanvasOptions,
    NgbOffcanvasRef,
} from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, Observable } from 'rxjs';
import {
    INotificationModel,
    NotificationActionType,
    NotificationType,
} from '@dxp/core/models';
import { environment } from '@dxp/core/environment';
import { SignalRHubService } from '../signalr.hub.service';

@Injectable({
    providedIn: 'root',
})
export class NotificationHubService extends SignalRHubService {
    readonly #http = inject(HttpClient);
    readonly #notificationPanelService = inject(NgbOffcanvas);
    readonly #notifications = signal<INotificationModel[]>([]);

    readonly #notificationAlertSubject =
        new BehaviorSubject<INotificationModel | null>(null);

    name = 'Notification';
    url = `${environment.blenderUrl}/notificationHub`;

    notifications = computed<INotificationModel[]>(() =>
        this.#notifications.asReadonly()(),
    );
    unread = computed<number>(
        () =>
            this.#notifications().filter(
                n => n.read === false && n.type === NotificationType.Tier3,
            ).length,
    );

    get notificationAlert(): Observable<INotificationModel | null> {
        return this.#notificationAlertSubject.asObservable();
    }

    addorUpdateNotification(notification: INotificationModel) {
        if (notification.type === NotificationType.Tier1) {
            this.#notificationAlertSubject.next(notification);
            return;
        }

        if (
            notification.type === NotificationType.Tier3 &&
            !notification.read &&
            this.#notificationPanelService.hasOpenOffcanvas()
        ) {
            this.markNotificationAs(notification, NotificationActionType.Read);
        }

        const existingIndex = this.#notifications().findIndex(
            n => n.id === notification.id,
        );

        if (existingIndex >= 0) {
            const update = [...this.#notifications()];
            update[existingIndex] = notification;
            this.#notifications.set(update.sort(this.#sort));
        } else {
            const update = [...this.#notifications(), notification];
            this.#notifications.set(update.sort(this.#sort));
        }
    }

    markNotificationAs(
        notification: INotificationModel,
        action: NotificationActionType,
    ) {
        this.#http
            .patch(
                environment.blenderUrl +
                    `/notification/${notification.id}/mark-as-${action}`,
                {},
            )
            .subscribe({
                next: () => {
                    this.logger.debug(
                        'Notification Hub Service (Notification) -> Successful',
                        `${notification.id} marked as ${action}`,
                    );
                },
                error: error => {
                    this.logger.error(
                        'Notification Hub Service (Notification) Failed ->',
                        error,
                    );
                },
            });
    }

    open(content: any, options?: NgbOffcanvasOptions): NgbOffcanvasRef {
        this.#notifications().forEach(notification => {
            if (
                notification.type === NotificationType.Tier3 &&
                !notification.read
            ) {
                this.markNotificationAs(
                    notification,
                    NotificationActionType.Read,
                );
            }
        });

        return this.#notificationPanelService.open(content, options);
    }

    removeNotification(notification: INotificationModel) {
        if (notification.type === NotificationType.Tier1) {
            this.#notificationAlertSubject.next(null);
            return;
        }

        const updatedNotifications = this.#notifications().filter(
            n => n.id !== notification.id,
        );

        this.#notifications.set(updatedNotifications);
    }

    protected override registerHandlers() {
        this.hubConnection.on(
            'Notification',
            (notification: INotificationModel) => {
                this.messageReceived();

                this.logger.debug(
                    'Notification Hub Service (Notification) ->',
                    notification,
                );
                this.logger.table(notification);

                this.addorUpdateNotification(notification);
            },
        );

        this.hubConnection.on('Remove', (notification: INotificationModel) => {
            this.messageReceived();

            this.logger.debug(
                'Notification Hub Service (Remove) ->',
                notification,
            );
            this.logger.table(notification);

            this.removeNotification(notification);
        });
    }

    protected override unregisterHandlers() {
        this.hubConnection.off('Notification');
        this.hubConnection.off('Remove');
    }

    #sort(a: INotificationModel, b: INotificationModel): number {
        const dateA = new Date(a.created);
        const dateB = new Date(b.created);

        return dateB.getTime() - dateA.getTime();
    }
}
